import { useAlert } from '@euler/functions/ux/useAlert';
import { delayed } from '@euler/utils';
import {
  useBehaviorSubject,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import React, {
  ReactNode,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { Fill } from 'react-slot-fill';
import { BehaviorSubject } from 'rxjs';
import { Loading } from './Loading';
import { LoadingProps } from './types';

export type LoadingContextProps = {
  subject: BehaviorSubject<LoadingProps | null>;
};

export const LoadingContext = React.createContext<LoadingContextProps>(
  null as any,
);

export const LoadingProvider = memo(
  ({ name = 'portal', children }: { name?: string; children: ReactNode }) => {
    const subject$ = useCreation(
      () => new BehaviorSubject<LoadingProps | null>(null),
      [],
    );
    const contextValue = useMemo(() => ({ subject: subject$ }), [subject$]);
    const [props] = useBehaviorSubject(subject$);
    const visible = Boolean(props);
    useEffect(() => {
      if (visible && (props?.duration || props?.type !== 'loading')) {
        return delayed(
          () => {
            subject$.next(null);
          },
          props?.duration ?? 1500,
        );
      }
    }, [props?.duration, props?.type, subject$, visible]);
    return (
      <LoadingContext.Provider value={contextValue}>
        {children}
        <Fill name={name}>
          {visible && <Loading {...props} visible={visible} />}
        </Fill>
      </LoadingContext.Provider>
    );
  },
);

export const useAppLoading = () => {
  const { subject } = useContext(LoadingContext);
  const alert = useAlert();

  const show = useCallback(
    (props?: string | LoadingProps) => {
      if (typeof props === 'string') {
        props = { type: 'loading', message: props };
      } else if (!props) {
        props = { type: 'loading' };
      }
      subject.next(props);
    },
    [subject],
  );

  const hide = useCallback(() => {
    subject.next(null);
  }, [subject]);

  const showWithAction = usePersistFn(
    async (
      action: () => any,
      options?: {
        delay?: number;
        onError?: (error: Error) => any;
      },
    ) => {
      let shown = false;
      let timer: any = setTimeout(
        () => {
          show();
          shown = true;
        },
        options?.delay ?? 50,
      );
      try {
        const result = await action();
        clearTimeout(timer);
        timer = undefined;
        if (shown) {
          hide();
        }
        return result;
      } catch (e) {
        clearTimeout(timer);
        timer = undefined;
        if (shown) hide();
        if (options?.onError) {
          // eslint-disable-next-line @typescript-eslint/return-await
          return options.onError(e as Error);
        } else {
          void alert({
            title: '出错了',
            message: (e as Error).message,
          });
        }
      }
    },
  );

  const controller = useMemo(
    () => ({ show, hide, showWithAction }),
    [show, hide, showWithAction],
  );

  return controller;
};
